import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { JobTaskView } from '@codebox/shared';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { onConnectionChange, subscribeProject } from '../lib/socket';
import { dismissNotice, isNoticeDismissed } from '../lib/notices';
import { recentRuntimeErrors } from '../lib/runtimeErrors';
import { IconChevronLeft } from '../components/icons';
import { ChatPanel, type Attachment, type TrailEntry, type ThreadWork } from '../components/studio/ChatPanel';
import { PreviewPane, type SelectedElement } from '../components/studio/PreviewPane';
import { type VersionRow } from '../components/studio/VersionStrip';
import { CARE_WORDS, PHASE_PCT, jobLabel, type CareCheckStatus, type JobRow } from '../components/studio/BackgroundWork';
import { runCareChecks } from '../lib/careChecks';
import { fetchTokens, TokenBalance, type TokenState } from '../components/studio/TokenMeter';
import { PublishDialog } from '../components/studio/PublishDialog';
import { CodePanel, type CodeFile } from '../components/studio/CodePanel';
import { CodeEditor } from '../components/studio/CodeEditor';
import { MediaPanel, type MediaKind } from '../components/studio/MediaPanel';
import { SpritePanel } from '../components/studio/SpritePanel';
import { StudioToolRail, type StudioPanel, type StudioTool } from '../components/studio/StudioToolRail';
import { SnapshotAnnotator } from '../components/studio/SnapshotAnnotator';
import { CollaboratorsButton } from '../components/studio/CollaboratorsButton';
import { SurveyPanel } from '../components/studio/SurveyPanel';
import { SparkyPanel, type SparkyMoment } from '../components/studio/SparkyPanel';
import { TestingMode } from '../components/studio/TestingMode';
import { MessagesPanel } from '../components/studio/MessagesPanel';
import { StudioToolbar } from '../components/studio/StudioToolbar';
import { DeviceFrame, type Device } from '../components/studio/DeviceFrame';
import { GenerationOverlay } from '../components/studio/GenerationOverlay';
import { BuildSummary, type BuildSummaryData } from '../components/studio/BuildSummary';
import { TaskListPanel } from '../components/studio/TaskListPanel';
import { StageDefine } from '../components/studio/StageDefine';
import { ChangeToasts, type Toast } from '../components/studio/ChangeToasts';

/** Client mirror of the server's forward-only task lifecycle (SPEC-P1-TASKLIST §2/§6). */
const TASK_RANK: Record<JobTaskView['status'], number> = {
  planned: 0,
  running: 1,
  done: 2,
  failed: 2,
  skipped: 2,
};

interface ProjectDetail {
  id: string;
  title: string;
  description: string | null;
  projectType: 'game' | 'app' | 'tool';
  status: 'draft' | 'published';
  visibility: 'private' | 'public';
  thumbnailUrl?: string | null;
  /** `profile.survey.done` decides whether the stage machine is still in SURVEY */
  profile?: { survey?: { done?: boolean; answers?: unknown[] } } | null;
  files?: Array<{ id: string; path: string }>;
}


/**
 * The studio, laid out like the old Code Box: a 56px frosted toolbar over `1fr 360px` — preview on
 * the LEFT inside a real device frame, a fixed-width edit rail on the RIGHT. Stage flow is
 * DEFINE → SURVEY → BUILD, with a full-screen overlay owning the wait while a build runs, and
 * change summaries arriving as toasts over the preview rather than as a chat transcript.
 */
export function StudioPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();

  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [entries, setEntries] = useState<TrailEntry[]>([]);
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  /* The Token balance, read once on mount and re-read whenever a job ends — which is exactly when
     it changes. No polling: the meter has one moment that matters and the socket already tells us
     when it arrives. */
  const [tokens, setTokens] = useState<TokenState | null>(null);
  const [bundleHtml, setBundleHtml] = useState<string | null>(null);
  const [activeVersionId, setActiveVersionId] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** 35f: which content panel is open on the tool rail, and the workspace interaction tool */
  const [panel, setPanel] = useState<StudioPanel>('ai');
  const [tool, setTool] = useState<StudioTool>('none');
  /** the workspace scroller inside DeviceFrame — the grab tool pans a zoomed device through it */
  const stageScrollRef = useRef<HTMLDivElement>(null);
  /** the file open in the workspace code editor (Codes panel drives it) */
  const [codeFile, setCodeFile] = useState<{ file: CodeFile; editable: boolean } | null>(null);
  /** 35g: element picked by the Select tool — becomes the target the next AI prompt acts on */
  const [selectedElement, setSelectedElement] = useState<SelectedElement | null>(null);
  /** 35g: captured preview image while the Snapshot tool's annotate overlay is open */
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  /** 35h: a media tile being dragged from the Media panel onto the preview */
  const [draggingMedia, setDraggingMedia] = useState<{ kind: MediaKind; url: string } | null>(null);
  /** a build or edit is in flight — several controls act on the app and must stand down for it */
  const busy = jobs.some(j => j.status === 'queued' || j.status === 'running');
  /*
   * Select and Edit text write to (or point at) the app, and a run is rewriting it underneath them:
   * an element picked mid-run belongs to a version about to be replaced. Drop the tool and the
   * selection when a run starts rather than letting either silently act on stale content.
   */
  useEffect(() => {
    if (!busy) return;
    setTool(current => (current === 'select' || current === 'text' ? 'none' : current));
    setSelectedElement(null);
  }, [busy]);

  /* First read of the balance. This is also what creates the wallet and pays the welcome grant,
     so a brand-new learner has Tokens before they type anything. */
  useEffect(() => {
    void fetchTokens().then(setTokens);
  }, []);
  /** 35i #3b: preview zoom (Sparked-style footer); 1 = fit */
  const [previewZoom, setPreviewZoom] = useState(1);
  const zoomPreview = (z: number) => setPreviewZoom(Math.min(2, Math.max(0.25, +z.toFixed(2))));
  /** 35i #5: undo/redo cursor into the version timeline (0 = latest). checkout walks it without appending. */
  const [versionCursor, setVersionCursor] = useState(0);
  const [publishOpen, setPublishOpen] = useState(false);
  const [sparkyDismissed, setSparkyDismissed] = useState(false);
  const [sparkyCustom, setSparkyCustom] = useState<{ title: string; body: string } | null>(null);
  const [liveNarration, setLiveNarration] = useState<string | null>(null);
  const [livePhase, setLivePhase] = useState<string | null>(null);
  /** P1 task list (SPEC-P1-TASKLIST §6): hydrated from the API on mount, then patched over WS. */
  const [tasks, setTasks] = useState<JobTaskView[]>([]);
  const tasksJobId = useRef<string | null>(null);
  /** freshest jobs for callbacks that must not re-create on every job change */
  const jobsRef = useRef<JobRow[]>([]);
  jobsRef.current = jobs;
  /** WS2: last sequence number seen per job — a jump means frames were dropped */
  const lastSeq = useRef<Map<string, number>>(new Map());
  /** the floating plan panel starts open — the plan is the point of the wait, not an extra click */
  const [tasksOpen, setTasksOpen] = useState(true);
  /** mobile only: the rail rides as a bottom sheet, collapsed to a peek until tapped */
  const [sheetOpen, setSheetOpen] = useState(false);
  const [device, setDevice] = useState<Device>('desktop');
  const [fullscreen, setFullscreen] = useState(false);
  const [view, setView] = useState<'build' | 'test'>('build');
  /** width of the resizable AI-Collaboration rail (now on the LEFT); persisted per browser (35e) */
  const RAIL_MIN = 320;
  const RAIL_MAX = 640;
  const [railWidth, setRailWidth] = useState(() => {
    const saved = Number(localStorage.getItem('cbx.railWidth'));
    return saved >= RAIL_MIN && saved <= RAIL_MAX ? saved : 380;
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [buildError, setBuildError] = useState<string | null>(null);
  /** socket has been down >10s — shown in the rail so a stalled build is never silently wrong */
  const [offline, setOffline] = useState(false);
  /** false until the first load resolves — the stage is UNKNOWN before that, so render nothing */
  const [loaded, setLoaded] = useState(false);
  /** the post-survey kickoff must fire exactly once, even as loadAll re-runs */
  const kickedOff = useRef(false);
  /*
   * True from the moment the survey is answered until the first job exists.
   *
   * startFirstBuild awaits loadAll() (which flips surveyDone true) BEFORE send() creates the job
   * (which flips busy true). In that gap the survey gate has closed but the build gate has not
   * opened, so the full studio rendered with no content for a beat and then snapped to the build
   * overlay. This keeps the handoff on one side of the fence or the other.
   */
  const [startingFirstBuild, setStartingFirstBuild] = useState(false);
  /*
   * Latched once the learner presses "Build it". Without it the review screen reappears for a beat
   * every time `busy` drops before `hasBuild` catches up — most visibly right after the first build
   * finishes, where the summary flashed back over the finished app.
   */
  const [surveyConfirmed, setSurveyConfirmed] = useState(false);
  /** the receipt shown when the FIRST build lands, before the studio takes over */
  const [buildSummary, setBuildSummary] = useState<BuildSummaryData | null>(null);
  const buildStartedAt = useRef<number | null>(null);
  /** 35c background care checks — one at a time, cancellable, shown in the rail's BackgroundWork */
  const [care, setCare] = useState<CareCheckStatus | null>(null);
  const [careDue, setCareDue] = useState<number | null>(null);
  const careAbort = useRef<AbortController | null>(null);
  /** job types by id, so the stream handler knows what kind of job just finished */
  const jobKinds = useRef<Map<string, { type: string; autofix: boolean }>>(new Map());
  /** transport-agnostic build-finished detection: a BUILD was running + a new version appeared */
  const generateInFlight = useRef(false);
  const versionCount = useRef<number | null>(null);

  const pushToast = useCallback((toast: Omit<Toast, 'id'>) => {
    setToasts(prev => [{ ...toast, id: `t${Date.now()}${Math.random()}` }, ...prev].slice(0, 6));
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const loadAll = useCallback(async () => {
    if (!projectId) return;
    try {
      const [projectRes, chatRes, versionsRes, jobsRes, bundleRes] = await Promise.all([
        api.get<{ project: ProjectDetail }>(`/box/projects/${projectId}`),
        api.get<{ entries: TrailEntry[] }>(`/box/projects/${projectId}/chat`),
        api.get<{ versions: VersionRow[] }>(`/box/projects/${projectId}/versions`),
        api.get<{ jobs: JobRow[] }>(`/box/projects/${projectId}/jobs`),
        api.get<{ html: string }>(`/box/projects/${projectId}/bundle`),
      ]);
      setProject(projectRes.project);
      setEntries(chatRes.entries);
      setVersions(versionsRes.versions);
      setJobs(jobsRes.jobs);
      for (const j of jobsRes.jobs) {
        jobKinds.current.set(j.id, { type: j.type, autofix: Boolean((j.payload as { autofix?: boolean } | undefined)?.autofix) });
        if (j.type === 'generate' && (j.status === 'queued' || j.status === 'running')) generateInFlight.current = true;
      }
      /*
       * Build-finished detection that works over ANY transport: a new version landed while a build
       * was in flight. The WS job.done path is faster when it works, but frames can be missed (socket
       * reconnects, backgrounded tabs) and the care checks must not silently skip because of that.
       */
      if (versionCount.current !== null && versionsRes.versions.length > versionCount.current && generateInFlight.current) {
        generateInFlight.current = false;
        setCareDue(Date.now());
      }
      versionCount.current = versionsRes.versions.length;
      setBundleHtml(bundleRes.html);
      setActiveVersionId(current => current ?? versionsRes.versions[0]?.id ?? null);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not open this project');
    } finally {
      setLoadingPreview(false);
      setLoaded(true);
    }
  }, [projectId]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  /*
   * 35c: background care checks after a build. One at a time; cancel aborts between steps. The
   * delay before starting gives the refreshed bundle time to load in the preview iframe — the checks
   * talk to THAT document, so they must not race its reload.
   */
  const startCareChecks = useCallback(() => {
    if (!projectId || careAbort.current) return;
    const controller = new AbortController();
    careAbort.current = controller;
    void runCareChecks({
      projectId,
      isGame: project?.projectType === 'game',
      signal: controller.signal,
      onPhase: phase => setCare({ phase }),
    }).then(result => {
      careAbort.current = null;
      setCare(null);
      if (result.cancelled) return;
      pushToast(
        result.jobQueued
          ? { kind: 'info', title: 'Sparky spotted something broken', lines: [result.summary] }
          : result.ok
            ? { kind: 'success', title: 'Looked it over — all good', lines: [result.summary] }
            : { kind: 'warning', title: 'Worth a look', lines: [result.summary] },
      );
    });
  }, [projectId, project?.projectType, pushToast]);

  const cancelCareChecks = useCallback(() => {
    careAbort.current?.abort();
    careAbort.current = null;
    setCare(null);
  }, []);

  // the 3.5s delay lets the refreshed bundle finish loading into the preview iframe first
  useEffect(() => {
    if (careDue === null) return;
    const timer = window.setTimeout(startCareChecks, 3500);
    return () => window.clearTimeout(timer);
  }, [careDue, startCareChecks]);

  useEffect(() => () => careAbort.current?.abort(), []);

  /*
   * WS2 — the SINGLE reconciliation path. Every recovery route calls this: the `joined` frame, a
   * sequence gap, the safety-net tick, and returning to a backgrounded tab.
   *
   * There used to be four near-copies of this merge, and they disagreed — the `joined` catch-up
   * refetched everything EXCEPT tasks, so a reconnect restored the job list and left the checklist
   * frozen. One function means a fix lands everywhere at once.
   */
  const reconcile = useCallback(
    async (reason: string) => {
      if (!projectId) return;
      await loadAll();
      const active = jobsRef.current.find(j => j.status === 'running' || j.status === 'queued');
      if (!active) return;
      try {
        const res = await api.get<{ tasks: JobTaskView[] }>(
          `/box/projects/${projectId}/jobs/${active.id}/tasks`,
        );
        if (res.tasks.length === 0) return;
        tasksJobId.current = active.id;
        setTasks(prev => {
          // forward-only, same rule as the WS path: a refetch must never un-tick a finished row
          const merged = res.tasks.map(next => {
            const old = prev.find(p => p.id === next.id);
            return old && TASK_RANK[old.status] > TASK_RANK[next.status] ? old : next;
          });
          const drift = merged.filter((m, i) => m.status !== prev[i]?.status).length;
          if (drift > 0) {
            // eslint-disable-next-line no-console
            console.info(`[cbx] reconcile (${reason}) corrected ${drift} task(s)`);
          }
          return merged;
        });
      } catch {
        /* the checklist is an enhancement — never surface a fetch error for it */
      }
    },
    [projectId, loadAll],
  );

  /*
   * Live job stream. WS1: this is the ONLY continuous source of job state — there is no periodic
   * poll behind it any more. Two frames make that safe: `joined` (below) tells us a subscribed
   * connection exists so we can catch up on whatever we missed, and `job.created` announces work the
   * server started on its own, which is the one thing the socket never used to carry and the sole
   * reason a 1.5s /jobs poll existed.
   */
  useEffect(() => {
    if (!projectId) return;
    return subscribeProject(projectId, user?.displayName ?? user?.email ?? 'Builder', frame => {
      /* Sent on every (re)join. Events emitted while the socket was down are gone for good, so the
         moment we know we are subscribed we reconcile against the database. */
      if (frame.t === 'joined') {
        void reconcile('joined'); // jobs AND tasks — the old catch-up refetched everything but tasks
        return;
      }
      if (frame.t !== 'job') return;
      const type = frame.type as string;

      /*
       * WS2 gap detection. A push stream cannot distinguish "quiet" from "I missed something", so a
       * dropped frame used to leave the UI wrong until some timer happened to notice. A jump in the
       * per-job counter says frames were lost, and we resync at once instead of in 30 seconds.
       */
      if (typeof frame.jobId === 'string' && typeof frame.seq === 'number') {
        const jobId = frame.jobId;
        const seq = frame.seq;
        const seen = lastSeq.current.get(jobId);
        if (seen !== undefined && seq > seen + 1) {
          // eslint-disable-next-line no-console
          console.info(`[cbx] frame gap on ${jobId}: ${seen} → ${seq}`);
          void reconcile('sequence gap');
        }
        if (seen === undefined || seq > seen) lastSeq.current.set(jobId, seq);
      }

      /* Server-started work — auto-fix, care check, feedback. The rail renders it immediately from
         the event; no fetch, because the payload already carries the row's shape. */
      if (type === 'job.created' && typeof frame.jobId === 'string') {
        const jobId = frame.jobId;
        const payload = (frame.payload ?? {}) as JobRow['payload'];
        const jobType = String(frame.jobType ?? 'edit');
        jobKinds.current.set(jobId, { type: jobType, autofix: Boolean(payload?.autofix) });
        if (jobType === 'generate') generateInFlight.current = true;
        setJobs(prev =>
          prev.some(j => j.id === jobId)
            ? prev
            : [
                {
                  id: jobId,
                  type: jobType,
                  phase: (frame.phase as string | null) ?? null,
                  status: (frame.status as JobRow['status']) ?? 'queued',
                  createdAt: String(frame.createdAt ?? new Date().toISOString()),
                  payload,
                },
                ...prev,
              ],
        );
      }
      if (type === 'job.phase') {
        if (typeof frame.phase === 'string') setLivePhase(frame.phase);
        if (typeof frame.narration === 'string') setLiveNarration(frame.narration);
        // the rail reads status/phase off its own row; without this a running job stays "queued"
        if (typeof frame.jobId === 'string') {
          const jobId = frame.jobId;
          const phase = typeof frame.phase === 'string' ? frame.phase : null;
          setJobs(prev => prev.map(j => (j.id === jobId ? { ...j, status: 'running', phase } : j)));
        }
      }
      /* P1: the checklist. 'job.tasks' replaces wholesale (seed / re-plan / fail-open);
         'task.update' is the per-row hot path. */
      if (type === 'job.tasks' && Array.isArray(frame.tasks)) {
        tasksJobId.current = String(frame.jobId ?? '');
        setTasks(frame.tasks as JobTaskView[]);
      }
      if (type === 'task.update' && typeof frame.taskId === 'string') {
        const taskId = frame.taskId;
        const status = frame.status as JobTaskView['status'];
        if (typeof frame.narration === 'string') setLiveNarration(frame.narration);
        // mirror the server's forward-only rule: a late frame must never un-tick a finished row
        setTasks(prev =>
          prev.map(task =>
            task.id === taskId && TASK_RANK[status] > TASK_RANK[task.status] ? { ...task, status } : task,
          ),
        );
      }
      if (type === 'job.done' || type === 'job.error' || type === 'job.cancelled') {
        setLiveNarration(null);
        setLivePhase(null);
        // the settle (or the release, on a failure) has landed by now
        void fetchTokens().then(setTokens);
        /* Drop it from the rail immediately. `/jobs` only returns queued+running, so the old poll
           removed finished work as a side effect of replacing the list wholesale; with the poll gone
           the row has to be retired here or it would sit there looking active until the next fetch. */
        if (typeof frame.jobId === 'string') {
          const jobId = frame.jobId;
          setJobs(prev => prev.filter(j => j.id !== jobId));
        }
        if (type === 'job.error' && typeof frame.message === 'string') {
          setBuildError(frame.message);
          pushToast({ kind: 'error', title: frame.message });
        }
        if (type === 'job.done') {
          setBuildError(null);
          pushToast({
            kind: 'success',
            title: 'Your change is in',
            lines: ['Have a look at the preview.'],
          });
          // a finished BUILD gets the care checks; fixes/edits don't, or a found defect would loop.
          // (loadAll's version-count detection is the fallback when this frame never arrives.)
          const finished = jobKinds.current.get(String(frame.jobId ?? ''));
          if (finished?.type === 'generate' && !finished.autofix) {
            generateInFlight.current = false;
            setCareDue(Date.now());
            /*
             * FIRST build only: show the receipt before the studio takes over. versionCount is the
             * pre-build count, so 0/null means this job created version #1. The usage rides on the
             * job.done frame itself — no follow-up fetch.
             */
            if ((versionCount.current ?? 0) === 0) {
              const usage = frame.usage as { inputTokens: number; outputTokens: number } | undefined;
              setTasks(current => {
                const summary = {
                  usage,
                  durationS: buildStartedAt.current ? Math.round((Date.now() - buildStartedAt.current) / 1000) : 0,
                  stepsDone: current.filter(t => t.status === 'done' || t.status === 'skipped').length,
                  stepsTotal: current.length,
                };
                /* Let the last stage visibly complete before the receipt replaces it. job.done can
                   land within a frame of the final task ticking, and swapping instantly meant the
                   stepper's fourth step never rendered as finished — the stage looked skipped. */
                window.setTimeout(() => setBuildSummary(summary), 900);
                return current; // read-only pass; setTasks is just the freshest-value channel
              });
            }
          }
        }
        void loadAll();
      }
    });
  }, [projectId, user?.displayName, user?.email, loadAll, reconcile, pushToast, startCareChecks]);

  /*
   * P1 (SPEC §6): hydrate the checklist for whatever job is already running when this page mounts —
   * a refresh mid-build must not show an empty panel (acceptance §8.4). WS deltas take over after.
   * Keyed on the active job id so a NEW job re-hydrates rather than inheriting the previous list.
   */
  const activeJobId = jobs.find(j => j.status === 'running' || j.status === 'queued')?.id ?? null;
  useEffect(() => {
    if (!projectId || !activeJobId || tasksJobId.current === activeJobId) return;
    let cancelled = false;
    let timer: number | null = null;
    let attempts = 0;

    /*
     * Drop the previous job's checklist the moment a new job takes over. It used to linger until the
     * replacement arrived, so starting an edit briefly showed the finished plan from the last build —
     * a list of ticked steps that had nothing to do with what was now running. Empty renders nothing
     * at all, which is the honest state until this job says what it intends to do.
     */
    setTasks([]);
    setTasksOpen(true); // each new job shows its plan; dismissing applies to that job, not forever

    /*
     * Catch-up for ONE known race, not a reinstated poll: a job is created 'queued' and its checklist
     * is not seeded until the plan returns (~8s), so the first fetch reliably comes back empty. The
     * old code latched `tasksJobId` on that empty result and never asked again, which left the WS
     * frame as the only path tasks could ever take — and a build finished behind a page still showing
     * its generic loading rows. This stops the instant tasks exist, and gives up after SEED_WINDOW;
     * steady-state progress still rides entirely on task.update frames.
     */
    const SEED_WINDOW = 10; // ~30s at 3s spacing — the plan phase is comfortably inside it

    const hydrate = async (): Promise<void> => {
      attempts += 1;
      try {
        const res = await api.get<{ tasks: JobTaskView[] }>(
          `/box/projects/${projectId}/jobs/${activeJobId}/tasks`,
        );
        if (cancelled) return;
        if (res.tasks.length > 0) {
          tasksJobId.current = activeJobId; // only latch once there is something to latch onto
          // a WS frame may have landed first; only fill in if we are still empty for this job
          setTasks(prev => (prev.length > 0 ? prev : res.tasks));
          return;
        }
      } catch {
        /* the list is an enhancement — never surface a fetch error for it */
      }
      if (cancelled || attempts >= SEED_WINDOW) return;
      timer = window.setTimeout(() => void hydrate(), 3000);
    };

    void hydrate();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [projectId, activeJobId]);

  /*
   * WS1: the adaptive 1.5s/5s `/jobs` poll that used to live here is gone. Job state now arrives as
   * events — `job.created` for work the server starts on its own, `job.phase` while it runs, and the
   * terminal frames below — and every (re)connection triggers one `loadAll()` via the `joined` frame,
   * which reconciles anything missed while the socket was down. The heartbeat in socket.ts is what
   * makes that safe: a half-open connection is now detected and closed instead of quietly stalling.
   */

  /*
   * Busy-only safety net — NOT a return of the poll. Events remain the transport; this exists for
   * exactly one failure: a terminal frame that never arrives. It happened in the field — all three
   * jobs finished server-side while the page showed "Fixing how it looks" for five minutes, because
   * with the poll deleted there was nothing left to notice. One reconcile every 30s, only while the
   * page believes work is running, caps that stall at 30s instead of forever. Idle costs nothing.
   */
  const anyWorkRunning = jobs.some(j => j.status === 'queued' || j.status === 'running');



  /* Last resort only. With gap detection and the visibility check below it should rarely correct
     anything; the drift counter logged above is the evidence for whether that holds. */
  useEffect(() => {
    if (!anyWorkRunning) return;
    const timer = window.setInterval(() => void reconcile('safety net'), 30_000);
    return () => window.clearInterval(timer);
  }, [anyWorkRunning, reconcile]);

  /* Coming back to a backgrounded tab: timers were throttled, so assume the stream missed things. */
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && anyWorkRunning) void reconcile('tab visible');
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [anyWorkRunning, reconcile]);

  /* The "reconnecting…" note: only after 10s of being down, so a routine blip stays invisible. */
  useEffect(() => {
    let timer: number | null = null;
    const stop = onConnectionChange(open => {
      if (timer) window.clearTimeout(timer);
      if (open) {
        setOffline(false);
        return;
      }
      timer = window.setTimeout(() => setOffline(true), 10_000);
    });
    return () => {
      if (timer) window.clearTimeout(timer);
      stop();
    };
  }, []);

  const send = async (
    prompt: string,
    mode: 'build' | 'plan' | 'debug',
    modelTier?: string,
    attachments?: Attachment[],
    snapshotFeedback?: { comments: string[] },
  ) => {
    if (!projectId) return;
    // 35g: if the learner selected an element, tell the AI exactly which one they mean. This rides as a
    // separate field so the visible chat bubble still shows only what the learner typed.
    let elementContext: string | undefined;
    if (selectedElement) {
      const el = selectedElement;
      const idPart = el.id ? `#${el.id}` : '';
      const clsPart = el.cls ? `.${el.cls.split(/\s+/).filter(Boolean).join('.')}` : '';
      const textPart = el.text ? ` containing the text "${el.text}"` : '';
      elementContext = `The user selected this element in the preview to change: <${el.tag}${idPart}${clsPart}>${textPart}.`;
    }
    try {
      const { job } = await api.post<{ job: JobRow }>(`/box/projects/${projectId}/chat`, {
        prompt,
        mode,
        // debug mode rides the app's recent runtime errors along as evidence
        consoleErrors: mode === 'debug' ? recentRuntimeErrors() : undefined,
        modelTier,
        attachments,
        elementContext,
        snapshotFeedback,
      });
      setSelectedElement(null); // the request now owns the selection
      jobKinds.current.set(job.id, { type: job.type, autofix: false });
      if (job.type === 'generate') generateInFlight.current = true;
      setJobs(prev => [job, ...prev]);
      setBuildError(null);
      const { entries: fresh } = await api.get<{ entries: TrailEntry[] }>(
        `/box/projects/${projectId}/chat`,
      );
      setEntries(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send that prompt');
    }
  };

  /** drag the divider to resize the left rail, clamped to [RAIL_MIN, RAIL_MAX] and persisted. */
  const startRailResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = railWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (ev: MouseEvent) => {
      const next = Math.min(RAIL_MAX, Math.max(RAIL_MIN, startW + (ev.clientX - startX)));
      setRailWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setRailWidth(w => {
        localStorage.setItem('cbx.railWidth', String(w));
        return w;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const stopJob = async (jobId: string) => {
    if (!projectId) return;
    try {
      await api.post(`/box/projects/${projectId}/jobs/${jobId}/cancel`);
      setJobs(prev => prev.filter(j => j.id !== jobId));
    } catch {
      setError('Could not stop that job');
    }
  };

  const reloadPreview = async () => {
    if (!projectId) return;
    setLoadingPreview(true);
    try {
      const { html } = await api.get<{ html: string }>(`/box/projects/${projectId}/bundle`);
      setBundleHtml(html);
    } finally {
      setLoadingPreview(false);
    }
  };

  // a real new version (build/edit) lands at the head → snap the undo cursor back to latest
  const headVersionId = versions[0]?.id;
  useEffect(() => {
    setVersionCursor(0);
  }, [headVersionId]);

  /**
   * 35i #5: undo/redo — checkout the neighbouring version (older = undo, newer = redo) in place. Some
   * "free" versions (media/text swaps) predate per-version snapshots and can't be checked out, so we skip
   * over them in the same direction until one restores (or we run out).
   */
  const stepVersion = async (delta: number) => {
    if (!projectId) return;
    for (let i = versionCursor + delta; i >= 0 && i < versions.length; i += delta) {
      try {
        await api.post(`/box/projects/${projectId}/versions/${versions[i].id}/checkout`, {});
        setVersionCursor(i);
        await reloadPreview();
        return;
      } catch {
        /* no snapshot on that version — keep walking */
      }
    }
    pushToast({ kind: 'info', title: delta > 0 ? 'Nothing earlier to undo to' : 'Nothing newer to redo to' });
  };

  const restoreVersion = async (versionId: string) => {
    if (!projectId) return;
    try {
      await api.post(`/box/projects/${projectId}/versions/${versionId}/restore`);
      pushToast({
        kind: 'info',
        title: 'Went back to that version',
        lines: ['Nothing was lost — this is a new step forward.'],
      });
      await loadAll();
      await reloadPreview();
    } catch (err) {
      pushToast({ kind: 'error', title: err instanceof Error ? err.message : 'Could not restore' });
    }
  };

  if (error && !project) {
    return (
      <div className="mx-auto max-w-3xl p-6">
        <Link
          to="/projects"
          className="inline-flex items-center gap-1 text-sm text-muted transition hover:text-ink"
        >
          <IconChevronLeft />
          Back to projects
        </Link>
        <div className="mt-4 rounded-2xl border border-line bg-surface p-4 text-sm text-muted">{error}</div>
      </div>
    );
  }

  /*
   * Only the RUNNING job's checklist. `tasks` is cleared when a new job takes over, and this second
   * guard means a plan is never shown against a job it does not belong to — the previous build's
   * finished steps used to linger under the next request.
   */
  const threadTasks = tasksJobId.current && tasksJobId.current === activeJobId ? tasks : [];
  const activeJob = jobs.find(j => j.status === 'running') ?? jobs.find(j => j.status === 'queued') ?? null;
  const hasBuild = versions.length > 0;
  // 35i #4: the single background task shown IN the AI thread (build/edit/fix or a care check)
  const threadWork: ThreadWork | null = activeJob
    ? {
        /* WS1 §3.3: with no poll behind it, a dead socket means this progress is frozen. Say so
           rather than letting a stalled bar read as slow work. */
        label: offline ? `${jobLabel(activeJob)} — reconnecting…` : jobLabel(activeJob),
        /* the diagnosis that queued this fix — 'Fixing how it looks' alone told the learner nothing */
        detail: (activeJob.payload as { error?: { message?: string } } | undefined)?.error?.message?.split('—')[0]?.slice(0, 120) ?? null,
        tasks: threadTasks,
        tasksOpen,
        onToggleTasks: threadTasks.length > 0 ? () => setTasksOpen(o => !o) : undefined,
        pct: PHASE_PCT[activeJob.phase ?? ''] ?? 8,
        indeterminate: activeJob.status === 'queued',
        waiting: jobs.filter(j => (j.status === 'queued' || j.status === 'running') && j.id !== activeJob.id).length,
        onStop: () => void stopJob(activeJob.id),
      }
    : care
      ? { label: CARE_WORDS[care.phase], pct: 0, indeterminate: true, waiting: 0, onStop: cancelCareChecks }
      : null;
  const teamUnread = 0; // 35f: wired to a real unread count in a follow-up
  const surveyDone = Boolean(project?.profile?.survey?.done);
  const stageLabel = !hasBuild ? (surveyDone ? 'Build' : 'Idea') : project?.status === 'published' ? 'Publish' : 'Build';

  /*
   * Survey answered → kick off the first build. The runner composes the real prompt from the
   * project description plus `profile.survey.answers`, so this only has to say "go".
   */
  const startFirstBuild = async () => {
    if (kickedOff.current) return;
    kickedOff.current = true;
    setStartingFirstBuild(true); // hold the handoff until the job exists (see the ref above)
    buildStartedAt.current = Date.now();
    setSurveyConfirmed(true); // the survey is behind us for good now
    try {
      await loadAll(); // pick up profile.survey.done so the survey gate closes
      await send('Build the first version from my idea and the answers I gave.', 'build');
    } finally {
      setStartingFirstBuild(false);
    }
  };

  const sparkyMoment: SparkyMoment = project?.status === 'published'
    ? 'first-publish'
    : versions.length > 0
      ? 'build-done'
      : 'welcome';

  /*
   * "Got it" has to mean it. Dismissal is remembered per project AND per moment, so publishing this
   * project silences "It is live" here for good without silencing it on a project the learner has
   * never published. An explained error (sparkyCustom) is deliberately NOT remembered — it is about
   * one specific failure, and the next one deserves to be seen.
   */
  const noticeKey = projectId ? `sparky:${projectId}:${sparkyMoment}` : null;
  const momentDismissed = !sparkyCustom && noticeKey !== null && isNoticeDismissed(noticeKey);

  /*
   * Stage machine: DEFINE → SURVEY → BUILD.
   *
   * Nothing renders until `loaded`, because the stage is genuinely unknown before the first load —
   * rendering the BUILD chrome optimistically is what made the studio flash into view and then snap
   * back to the Define question.
   */
  if (!loaded) {
    return (
      <div className="grid min-h-full place-items-center p-8">
        <div className="w-full max-w-[720px] space-y-3">
          <div className="skeleton h-8 w-52 rounded-field" />
          <div className="skeleton h-40 w-full rounded-card" />
        </div>
      </div>
    );
  }

  /* DEFINE — only reachable for legacy rows with no idea recorded; /projects/new owns this now. */
  if (project && !project.description && !hasBuild && entries.length === 0 && !busy) {
    return (
      <StageDefine
        name={user?.displayName ?? null}
        busy={busy}
        onStart={idea => {
          void (async () => {
            await api.patch(`/box/projects/${projectId}`, { description: idea });
            await loadAll();
          })();
        }}
      />
    );
  }

  /*
   * SURVEY — between "describe it" and "build it"; the build starts only once the learner confirms.
   *
   * Deliberately NOT gated on `surveyDone`: the server marks the survey done when the last question
   * is answered, but the panel then shows the review screen, and closing this gate on `done` would
   * unmount it mid-review. SurveyPanel decides whether to show a question or the summary; the build
   * gates below take over the moment onDone fires.
   */
  if (project && !hasBuild && !busy && !startingFirstBuild && !surveyConfirmed) {
    return (
      <div className="grid h-full place-items-center overflow-auto bg-page p-4 sm:p-8">
        {projectId && <SurveyPanel projectId={projectId} onDone={() => void startFirstBuild()} />}
      </div>
    );
  }

  const PANEL_TITLE: Record<StudioPanel, string> = {
    ai: 'AI Collaboration',
    media: 'Media',
    sprites: 'Sprite Generator',
    codes: 'Codes',
    history: 'History',
    team: 'Team',
  };
  /** the workspace-tool badge shown in the panel header (select shows none — it is the resting mode) */
  const TOOL_CHIP: Partial<Record<StudioTool, string>> = { grab: 'Pan mode', text: 'Edit text', snapshot: 'Snapshot' };

  const railBody = (
    <>
      {/*
        The checklist used to be pinned here, above the thread and detached from the message that
        caused it. It now hangs off the running work bubble as a drawer (ChatPanel's WorkRow), which
        keeps the steps next to the request and stops a long plan from crowding out the conversation.
      */}
      {panel === 'ai' && (
        <ChatPanel
          entries={entries}
          busy={busy}
          canPinModel={user?.plan === 'pro'}
          meId={user?.id}
          onSend={send}
          selected={selectedElement}
          onClearSelected={() => setSelectedElement(null)}
          work={hasBuild ? threadWork : null}
          hasBuild={hasBuild}
          tokens={tokens}
        />
      )}
      {panel === 'media' && projectId && (
        <MediaPanel projectId={projectId} onDragMedia={setDraggingMedia} onSaved={() => void loadAll()} />
      )}
      {panel === 'sprites' && projectId && <SpritePanel projectId={projectId} onSaved={() => void loadAll()} />}
      {panel === 'codes' && projectId && (
        <CodePanel
          projectId={projectId}
          selectedId={codeFile?.file.id ?? null}
          onOpenFile={(file, editable) => setCodeFile({ file, editable })}
        />
      )}
      {panel === 'history' && (
        <div className="min-h-0 flex-1 overflow-auto p-3">
          {/* only the last 10 MAJOR updates (versions); small text/image tweaks use undo/redo (35f) */}
          {versions.length === 0 ? (
            <p className="text-sm text-muted">
              No edits yet. Your history will show up here so you can jump back to any version.
            </p>
          ) : (
            <ul className="space-y-1">
              {versions.slice(0, 10).map((version, i) => (
                <li key={version.id} className="flex items-center gap-2 rounded-xl px-2.5 py-2 transition hover:bg-page">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">
                      v{version.seq} · {version.label ?? version.author}
                    </span>
                    <span className="block text-[11px] text-muted">
                      {new Date(version.createdAt).toLocaleString()}
                    </span>
                  </span>
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => void restoreVersion(version.id)}
                      className="shrink-0 rounded-lg border border-line px-2 py-1 text-xs font-semibold transition hover:bg-page"
                    >
                      Restore
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {panel === 'team' && projectId && <MessagesPanel projectId={projectId} />}
    </>
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-page">
      {!fullscreen && (
        <StudioToolbar
          title={project?.title ?? 'Loading'}
          stageLabel={stageLabel}
          device={device}
          onDevice={setDevice}
          onReload={() => void reloadPreview()}
          onFullscreen={() => setFullscreen(true)}
          published={project?.status === 'published'}
          onPublish={() => setPublishOpen(true)}
          onUndo={() => void stepVersion(1)}
          onRedo={() => void stepVersion(-1)}
          canUndo={hasBuild && versionCursor < versions.length - 1 && !busy}
          canRedo={versionCursor > 0 && !busy}
          /* The one number in the chrome that is about the PERSON rather than the work, so it sits
             with the account controls rather than with undo and the device toggles. */
          right={<TokenBalance tokens={tokens} />}
          collaborators={projectId && <CollaboratorsButton projectId={projectId} meId={user?.id} />}
          /* The spark gauges and the Build/Design toggle are gone from the chrome — they read as
             dashboard furniture next to the work itself. `view` state and TestingMode are untouched,
             so nothing about how they behave has changed; only the toolbar stopped advertising them. */
        />
      )}

      {publishOpen && projectId && project && (
        <PublishDialog
          projectId={projectId}
          project={{
            status: project.status,
            visibility: project.visibility,
            thumbnailUrl: project.thumbnailUrl ?? null,
            title: project.title,
          }}
          onClose={() => setPublishOpen(false)}
          onChanged={() => void loadAll()}
        />
      )}

      {/* the full-screen modal is the FIRST-build experience only; later work runs in the rail (35d) */}
      {buildSummary && project && (
        <BuildSummary
          title={project.title}
          description={project.description}
          fileCount={(project.files ?? []).filter(f => f.path.startsWith('src/')).length}
          data={buildSummary}
          onContinue={() => setBuildSummary(null)}
        />
      )}
      {(busy || startingFirstBuild) && !hasBuild && !buildSummary && (
        <GenerationOverlay
          phase={livePhase ?? activeJob?.phase ?? null}
          narration={liveNarration}
          tasks={tasks}
          offline={offline}
          error={buildError}
          onCancel={() => activeJob && void stopJob(activeJob.id)}
          onRetry={() => {
            setBuildError(null);
            void loadAll();
          }}
        />
      )}

      {/*
        The post-build "Have a play with it" nudge is gone: after a build the learner is already
        looking at the thing they just made, so a banner telling them to look at it only costs space.
        Sparky still speaks when it has something the learner cannot see for themselves — an
        explained error (sparkyCustom), or the first publish.
      */}
      {!fullscreen && !sparkyDismissed && !momentDismissed && !busy && (sparkyCustom !== null || sparkyMoment !== 'build-done') && (
        <SparkyPanel
          moment={sparkyMoment}
          narration={null}
          custom={sparkyCustom}
          onDismiss={() => {
            if (!sparkyCustom && noticeKey) dismissNotice(noticeKey); // remembered past this session
            setSparkyCustom(null);
            setSparkyDismissed(true);
          }}
        />
      )}


      {view === 'test' && projectId && !fullscreen ? (
        <div className="min-h-0 flex-1 bg-surface">
          <TestingMode projectId={projectId} onSent={() => void loadAll()} />
        </div>
      ) : (
        <div
          className={fullscreen ? 'studio-shell flex min-h-0 flex-1 flex-col' : 'studio-shell flex min-h-0 flex-1 flex-col lg:flex-row'}
          style={fullscreen ? undefined : ({ ['--rail-w' as string]: `${railWidth}px` } as React.CSSProperties)}
        >
          {/* far-left tool rail (35f) + the content panel it drives — LEFT on desktop, resizable */}
          {!fullscreen && (
            <StudioToolRail
              panel={panel}
              tool={tool}
              onPanel={p => {
                setPanel(p);
                setTool('none'); // opening a panel drops any workspace tool so the preview stays playable
                setSnapshotUrl(null);
              }}
              onTool={t => {
                // clicking the already-active tool turns it off (back to the normal playable preview)
                const next = t === tool ? 'none' : t;
                setTool(next);
                if (next !== 'snapshot') setSnapshotUrl(null); // leaving snapshot closes any open annotate overlay
              }}
              teamUnread={teamUnread}
              busy={busy}
            />
          )}
          {/*
            Below lg the rail is a BOTTOM SHEET, not a stacked column — the same pattern Sparked's
            editor uses. Stacked, it ate the whole screen and pushed the preview out of view, which
            is the opposite of what a phone needs: the app you are building should stay visible and
            the conversation should slide over it on demand. Desktop is unchanged.
          */}
          {!fullscreen && (
            <aside
              className={[
                'flex min-h-0 flex-col bg-surface',
                /* the max-height animation is a MOBILE gesture (sheet open/close); desktop must not
                   carry it, or crossing the lg breakpoint animates the un-capping and tears */
                'fixed inset-x-0 bottom-0 z-30 rounded-t-2xl shadow-card transition-[max-height] duration-300 ease-out lg:transition-none',
                sheetOpen ? 'max-h-[82vh]' : 'max-h-[68px]',
                /* desktop: back to a plain column, no fixed positioning, no cap */
                'lg:static lg:z-auto lg:max-h-none lg:w-[var(--rail-w)] lg:rounded-none lg:shadow-none',
              ].join(' ')}
              style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            >
              {/* grab handle — the whole header is the toggle on touch, a plain header on desktop */}
              <button
                type="button"
                onClick={() => setSheetOpen(o => !o)}
                aria-expanded={sheetOpen}
                className="flex shrink-0 flex-col items-stretch px-4 pb-2 pt-2.5 text-left lg:pointer-events-none lg:pt-4"
              >
                <span className="mx-auto mb-2 h-1 w-9 shrink-0 rounded-full bg-line lg:hidden" />
                <span className="flex items-center justify-between">
                  <span className="text-sm font-bold text-ink">{PANEL_TITLE[panel]}</span>
                  <span className="flex items-center gap-2">
                    {TOOL_CHIP[tool] && (
                      <span className="rounded-full bg-accent/15 px-2.5 py-0.5 text-[11px] font-bold text-accent-deep">{TOOL_CHIP[tool]}</span>
                    )}
                    <span className="text-[11px] font-semibold text-muted lg:hidden">{sheetOpen ? 'Hide' : 'Show'}</span>
                  </span>
                </span>
              </button>
              <div className="flex min-h-0 flex-1 flex-col">{railBody}</div>
            </aside>
          )}

          {/* drag-to-resize divider — desktop only */}
          {!fullscreen && (
            <div
              onMouseDown={startRailResize}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize panel"
              className="hidden w-1.5 shrink-0 cursor-col-resize bg-line/50 transition-colors hover:bg-accent lg:block"
            />
          )}

          {/* preview — fills the rest, inside a real device frame. Outer = positioning context (holds the
              flushed zoom footer); inner = the scrolling workspace (pb-14 clears the footer). */}
          <div className="relative min-h-0 flex-1 bg-page pb-[68px] lg:pb-0">
            {/*
              The code editor takes the WORKSPACE, not the rail — the rail is the explorer. It
              overlays the preview (which stays mounted underneath, keeping the running app's state)
              and closing it drops straight back to the live app.
            */}
            {panel === 'codes' && codeFile && projectId && (
              <CodeEditor
                projectId={projectId}
                file={codeFile.file}
                editable={codeFile.editable}
                onSaved={() => void loadAll()}
                onClose={() => setCodeFile(null)}
              />
            )}
            {/*
              The plan, floating over the workspace rather than inside the thread. It is a second
              level: open by default while work is running, dismissable, and toggled by the work
              bubble in the rail. Living here means a seven-step plan neither pushes the conversation
              around nor gets squeezed into the rail's width.
            */}
            {threadTasks.length > 0 && tasksOpen && (
              <div className="anim-in pointer-events-auto absolute left-4 top-4 z-20 w-[340px] max-w-[calc(100%-2rem)]">
                <TaskListPanel
                  tasks={threadTasks}
                  narration={busy ? liveNarration : null}
                  className="max-h-[60vh] overflow-auto shadow-card"
                  onClose={() => setTasksOpen(false)}
                />
              </div>
            )}
            {/* Uniform wrapper across all modes: DeviceFrame measures this box and resolves every
                mode to pixels, so desktop↔tablet↔mobile is one continuous width/height morph. Zoom
                is handed to the frame rather than applied here, because it scales the DEVICE
                (true-scale, aspect locked) and the frame owns its own scroller for the overflow. */}
            <div className="dot-grid absolute inset-0 p-4 pb-14">
              <div className="h-full w-full">
                <DeviceFrame device={device} zoom={previewZoom} scrollerRef={stageScrollRef}>
                <PreviewPane
                  projectId={projectId!}
                  html={bundleHtml}
                  loading={loadingPreview}
                  chromeless
                  tool={tool}
                  onSelectElement={el => {
                    setSelectedElement(el);
                    if (el) setPanel('ai'); // surface the thread so the "selected" chip is visible
                  }}
                  onTextEdit={ok =>
                    pushToast(
                      ok
                        ? { kind: 'success', title: 'Text updated' }
                        : { kind: 'warning', title: 'Could not update that text' },
                    )
                  }
                  onSnapshot={dataUrl => {
                    if (dataUrl) {
                      setSnapshotUrl(dataUrl);
                    } else {
                      pushToast({ kind: 'warning', title: 'Could not capture the preview' });
                      setTool('select');
                    }
                  }}
                  /* grab-drag the app itself could not absorb pans the workspace, which is where a
                     zoomed device overflows — the preview iframe cannot scroll its own container */
                  onPan={(dx, dy) => {
                    const el = stageScrollRef.current;
                    if (!el) return;
                    el.scrollLeft += dx;
                    el.scrollTop += dy;
                  }}
                  draggingMedia={draggingMedia}
                  onMediaDropped={ok =>
                    pushToast(ok ? { kind: 'success', title: 'Media swapped' } : { kind: 'warning', title: 'Drop it onto a matching element' })
                  }
                  onReload={() => void reloadPreview()}
                  onEscalate={(plain, nudge) => {
                    setSparkyDismissed(false);
                    setSparkyCustom({ title: plain, body: nudge });
                  }}
                  onAutofix={() => {
                    pushToast({ kind: 'warning', title: 'Something broke — fixing it for free' });
                  }}
                />
                </DeviceFrame>
              </div>
            </div>

            <ChangeToasts toasts={toasts} onDismiss={dismissToast} />

            {/* 35g: Snapshot tool — annotate a picture of the preview, then send all notes to the AI */}
            {snapshotUrl && tool === 'snapshot' && (
              <SnapshotAnnotator
                src={snapshotUrl}
                busy={busy}
                authorName={user?.displayName || user?.email || 'You'}
                onClose={() => {
                  setSnapshotUrl(null);
                  setTool('select');
                }}
                onSend={async (prompt, attachments, comments) => {
                  await send(prompt, 'build', undefined, attachments, { comments });
                  setSnapshotUrl(null);
                  setTool('select');
                  setPanel('ai');
                }}
              />
            )}

            {/* 35i #3b: full-width zoom footer flushed to the bottom — matches Sparked v2's ZoomStage bar */}
            {!(snapshotUrl && tool === 'snapshot') && !fullscreen && (
              <div className="absolute inset-x-0 bottom-0 z-20 flex items-center gap-3 border-t border-line bg-surface px-3 py-2">
                <div className="min-w-0 flex-1" />
                <div className="flex shrink-0 items-center gap-1.5">
                  <button type="button" onClick={() => zoomPreview(previewZoom - 0.1)} disabled={previewZoom <= 0.25} aria-label="Zoom out" title="Zoom out" className="grid h-7 w-7 place-items-center rounded-full text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /></svg>
                  </button>
                  <input type="range" min={25} max={200} step={1} value={Math.round(previewZoom * 100)} onChange={e => zoomPreview(Number(e.target.value) / 100)} aria-label="Zoom" className="h-1.5 w-28 cursor-pointer accent-accent sm:w-36" />
                  <button type="button" onClick={() => zoomPreview(previewZoom + 0.1)} disabled={previewZoom >= 2} aria-label="Zoom in" title="Zoom in" className="grid h-7 w-7 place-items-center rounded-full text-muted transition hover:bg-page hover:text-ink disabled:opacity-40">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                  </button>
                  <button type="button" onClick={() => setPreviewZoom(1)} title="Reset to fit" className="min-w-[3.25rem] rounded-md px-1.5 py-1 text-xs font-semibold tabular-nums text-ink transition hover:bg-page">
                    {Math.round(previewZoom * 100)}%
                  </button>
                  <span className="mx-0.5 h-4 w-px bg-line" />
                  <button
                    type="button"
                    onClick={() => setPreviewZoom(1)}
                    aria-label="Fit to screen"
                    title="Fit to screen"
                    className={['grid h-7 w-7 place-items-center rounded-full transition', previewZoom === 1 ? 'bg-accent/15 text-accent-deep' : 'text-muted hover:bg-page hover:text-ink'].join(' ')}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 9V5a1 1 0 0 1 1-1h4M15 4h4a1 1 0 0 1 1 1v4M20 15v4a1 1 0 0 1-1 1h-4M9 20H5a1 1 0 0 1-1-1v-4" /></svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewZoom(1)}
                    aria-label="Actual size"
                    title="Actual size (100%)"
                    className={['grid h-7 place-items-center rounded-md px-1.5 text-[11px] font-bold transition', previewZoom === 1 ? 'bg-accent/15 text-accent-deep' : 'text-muted hover:bg-page hover:text-ink'].join(' ')}
                  >
                    1:1
                  </button>
                </div>
              </div>
            )}

            {fullscreen && (
              <button
                type="button"
                onClick={() => setFullscreen(false)}
                className="absolute right-4 top-4 z-30 rounded-full bg-ink/80 px-3 py-1.5 text-xs font-semibold text-surface backdrop-blur transition hover:bg-ink"
              >
                Exit full screen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
